import React, { Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { delay } from "@formant/core/common/delay";
import { fork } from "@formant/core/common/fork";
import { IDeviceDetails } from "@formant/core/model/IDeviceDetails";
import { animate } from "@formant/ui/utils/animate";

import {
    appStore,
    deviceDetailsStore,
    groupStore,
    organizationStore,
    tagTemplateStore,
} from "../../stores";
import { FeatureFlagLocalStorageManager } from "../../utils/FeatureFlagLocalStorageManager/FeatureFlagLocalStorageManager";
import { FeatureFlagsLocalStorageEnum } from "../../utils/FeatureFlagLocalStorageManager/FeatureFlagsLocalStorage.enum";
import { NewSidebar } from "../NewSidebar/fleet-sidebar";
import { ITagsAndGroups } from "../NewSidebar/fleet-sidebar.types";
import * as styles from "./index.scss";
import { JumpPopup } from "./JumpPopup";
import { SidebarContent } from "./SidebarContent";

interface ISidebarProps {
    open?: boolean;
}

@observer
export class Sidebar extends Component<ISidebarProps> {
    @observable private wasRecentlyOpen = false;
    @observable private closing = false;
    @observable private tagsAndGroups: ITagsAndGroups[] = [];
    @observable private devicesData: IDeviceDetails[] = [];
    public render() {
        const { onClose, closing, setWasRecentlyOpen, wasRecentlyOpen } = this;
        const displayContent = appStore.sidebarIsOpen || wasRecentlyOpen;
        fork(setWasRecentlyOpen());

        const organization = organizationStore.authenticated();
        const sidebarV2Enabled =
            (organization && organization.sidebarV2Enabled) ||
            FeatureFlagLocalStorageManager.getFlag(
                FeatureFlagsLocalStorageEnum.EnableSidebarV2Enabled,
            );
        return (
            <>
                <div
                    className={classnames(
                        "fixed ",
                        appStore.sidebarIsOpen
                            ? "animate-slideIn top-0 "
                            : "animate-slideOut",

                        styles.sidebar,
                    )}
                    style={{
                        left: appStore.sidebarIsOpen ? 0 : undefined,
                    }}
                >
                    {displayContent &&
                        (sidebarV2Enabled ? (
                            <NewSidebar
                                handleSideBarClose={onClose}
                                tagsAndGroups={this.tagsAndGroups}
                                devicesData={this.devicesData}
                            />
                        ) : (
                            <SidebarContent onClose={onClose} />
                        ))}
                </div>
                <div
                    className={classnames(
                        styles.overlay,
                        closing && styles.closing,
                        appStore.sidebarIsOpen && styles.open,
                    )}
                    onClick={onClose}
                />
                <JumpPopup close={onClose} />
            </>
        );
    }

    public async componentDidMount() {
        if (this.props.open && !appStore.sidebarIsOpen) {
            await this.open();
        } else {
            await this.fetchDevicesData();
            void this.fetchTagsAndGroups();
        }
    }

    private fetchTagsAndGroups = async () => {
        const organization = organizationStore.authenticated();
        const sidebarV2Enabled =
            (organization && organization.sidebarV2Enabled) ||
            FeatureFlagLocalStorageManager.getFlag(
                FeatureFlagsLocalStorageEnum.EnableSidebarV2Enabled,
            );
        if (!sidebarV2Enabled) {
            return;
        }
        const [templates, groups] = await Promise.all([
            tagTemplateStore.listAsync(),
            groupStore.fetchList(),
        ]);
        const data: ITagsAndGroups[] = templates
            .map(template => ({
                tag: template,
                groups: groups
                    .filter(
                        _ =>
                            _.tagKey === template.tagKey &&
                            _.active &&
                            _.enabled,
                    )
                    .map(group => ({
                        ...group,
                        devices: this.devicesData.filter(
                            device =>
                                device.tags[group.tagKey] === group.tagValue,
                        ),
                    })),
            }))
            .filter(_ => _.tag.isGroup && _.groups.length > 0);

        this.tagsAndGroups = data;
    };

    private fetchDevicesData = async () => {
        const organization = organizationStore.authenticated();
        const sidebarV2Enabled =
            (organization && organization.sidebarV2Enabled) ||
            FeatureFlagLocalStorageManager.getFlag(
                FeatureFlagsLocalStorageEnum.EnableSidebarV2Enabled,
            );
        if (!sidebarV2Enabled) {
            return;
        }
        const devices = await deviceDetailsStore.recentAsync();
        this.devicesData = devices;
    };

    private open = async () => {
        appStore.sidebarIsOpen = true;
        await this.fetchDevicesData();
        void this.fetchTagsAndGroups();
    };

    private onClose = async () => {
        this.closing = true;
        await animate();
        appStore.sidebarIsOpen = false;
        this.closing = false;
    };

    private setWasRecentlyOpen = async () => {
        await delay();
        if (appStore.sidebarIsOpen) {
            this.wasRecentlyOpen = true;
        } else if (this.wasRecentlyOpen) {
            await this.animateClose();
        }
    };

    private animateClose = async () => {
        await animate();
        this.wasRecentlyOpen = false;
    };
}
